import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { HandbookListItem } from "@/types/handbook";

interface HandbookRelatedPickerProps {
  /** 현재 선택된 관련 항목 _id 배열. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** 전체 handbook 항목 (검색에 쓰임) */
  items: HandbookListItem[];
  /** 편집 중인 handbook id (자기 자신 제외) */
  excludeId?: string;
  disabled?: boolean;
  /** 최대 선택 수 (백엔드는 10) */
  maxSelections?: number;
}

/**
 * 관련 문서 multi-select picker.
 *  - parent picker 와 달리 위계(트리/사이클) 무관 — flat list.
 *  - 정렬: 제목 사전순.
 *  - 자기 자신만 차단 (자손 차단 없음 — 관련 문서는 위계가 아니라 추천).
 */
export function HandbookRelatedPicker({
  value,
  onChange,
  items,
  excludeId,
  disabled,
  maxSelections = 10,
}: HandbookRelatedPickerProps) {
  const [open, setOpen] = useState(false);

  const sorted = useMemo(() => {
    return items
      .filter((it) => it._id !== excludeId)
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [items, excludeId]);

  const itemById = useMemo(() => {
    const m = new Map<string, HandbookListItem>();
    for (const it of items) m.set(it._id, it);
    return m;
  }, [items]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      if (value.length >= maxSelections) return;
      onChange([...value, id]);
    }
  };

  const removeOne = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        관련 문서 (선택, 여러 개 가능 — 위계 아님, 추천 사이드바용)
      </label>

      {/* 선택된 관련 문서 뱃지 */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => {
            const item = itemById.get(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
              >
                {item ? item.title : `(${id.slice(-6)})`}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeOne(id)}
                    className="opacity-60 hover:opacity-100"
                    aria-label="제거"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="text-muted-foreground">
              {value.length === 0
                ? "관련 문서 없음"
                : `${value.length}개 선택됨 — 추가/해제`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="제목으로 검색..." />
            <CommandList>
              <CommandEmpty>일치하는 항목 없음</CommandEmpty>
              <CommandGroup>
                {sorted.map((item) => {
                  const checked = selectedSet.has(item._id);
                  return (
                    <CommandItem
                      key={item._id}
                      value={`${item.title} ${item.slug}`}
                      onSelect={() => toggle(item._id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          checked ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{item.title}</span>
                      <code className="ml-auto text-[10px] text-muted-foreground">
                        {item.slug}
                      </code>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
